import { useTranslation } from "react-i18next";
import {
  Ban,
  CreditCard,
  FileExclamationPoint,
  Home,
  KeyRound,
  type LucideIcon,
} from "lucide-react";
import { HoverPopover } from "@/components/ui";
import { cn } from "@/lib/utils";
import {
  getSetupBadgeKind,
  getSetupMissingItems,
  getTelemetryStatus,
  telemetryStatusDotClass,
  type SetupMissingKind,
  type TelemetryStatus,
} from "@/lib/deviceStatus";
import type { Device } from "@/types";

type DeviceRow = Pick<
  Device,
  | "software_id"
  | "last_login_at"
  | "last_device_report_at"
  | "device_status_payload"
  | "device_location_payload"
  | "house_id"
  | "is_revoked"
> & {
  reader?: Device["reader"];
};

const TELEMETRY_TOOLTIP_KEYS: Record<TelemetryStatus, string> = {
  gray: "page.telemetryTooltipGray",
  green: "page.telemetryTooltipGreen",
  yellow: "page.telemetryTooltipYellow",
  red: "page.telemetryTooltipRed",
};

const MISSING_ITEM_KEYS: Record<SetupMissingKind, string> = {
  binding: "page.setupMissingBinding",
  pairing: "page.setupMissingPairing",
  assignment: "page.setupMissingAssignment",
};

const MISSING_ITEM_DESC_KEYS: Record<SetupMissingKind, string> = {
  binding: "page.setupMissingBindingDesc",
  pairing: "page.setupMissingPairingDesc",
  assignment: "page.setupMissingAssignmentDesc",
};

const MISSING_ITEM_ICONS: Record<SetupMissingKind, LucideIcon> = {
  binding: KeyRound,
  pairing: CreditCard,
  assignment: Home,
};

export function DeviceTelemetryDot({ device }: { device: DeviceRow }) {
  const { t } = useTranslation("devices");
  const status = getTelemetryStatus(device);
  const label = t(TELEMETRY_TOOLTIP_KEYS[status]);

  return (
    <HoverPopover content={label} placement="above">
      <span
        className="-m-1 inline-flex shrink-0 cursor-default items-center justify-center rounded-full p-1"
        aria-label={label}
      >
        <span
          className={cn(
            "h-2.5 w-2.5 rounded-full",
            telemetryStatusDotClass(status),
          )}
          aria-hidden
        />
      </span>
    </HoverPopover>
  );
}

export function DeviceSetupBadge({ device }: { device: DeviceRow }) {
  const { t } = useTranslation("devices");
  const kind = getSetupBadgeKind(device);

  if (kind === "none") {
    return null;
  }

  if (kind === "revoked") {
    const label = t("page.setupBadgeRevoked");
    return (
      <HoverPopover
        content={t("page.setupBadgeRevokedTooltip")}
        placement="below"
      >
        <span
          className="inline-flex shrink-0 cursor-default items-center justify-center rounded-md p-1 text-error-600 transition hover:bg-error-50"
          aria-label={label}
        >
          <Ban className="h-4 w-4" aria-hidden />
        </span>
      </HoverPopover>
    );
  }

  const missing = getSetupMissingItems(device);
  const label = t("page.setupBadgeIncomplete");
  const content = (
    <div className="max-w-[16rem] space-y-2.5 text-left">
      <div className="space-y-1">
        <p className="text-sm font-semibold text-surface-900">
          {t("page.statusPartial")}
        </p>
        <p className="text-xs font-normal leading-snug text-surface-600">
          {t("page.setupBadgeIncompleteTooltipDesc")}
        </p>
      </div>
      <div className="space-y-1.5">
        <p className="text-xs font-medium text-surface-500">
          {t("page.setupBadgeIncompleteTooltipTitle")}
        </p>
        <ul className="space-y-2">
          {missing.map((item) => {
            const Icon = MISSING_ITEM_ICONS[item];
            return (
              <li key={item} className="flex gap-2">
                <Icon
                  className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning-600"
                  aria-hidden
                />
                <div className="min-w-0">
                  <p className="text-xs font-medium text-surface-900">
                    {t(MISSING_ITEM_KEYS[item])}
                  </p>
                  <p className="text-[11px] font-normal leading-snug text-surface-500">
                    {t(MISSING_ITEM_DESC_KEYS[item])}
                  </p>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );

  return (
    <HoverPopover
      content={content}
      placement="below"
      contentClassName="rounded-lg border border-surface-200 bg-surface-0 p-3 text-surface-900 shadow-popover"
    >
      <span
        className="inline-flex shrink-0 cursor-default items-center justify-center rounded-md p-1 text-warning-600 transition hover:bg-warning-50"
        aria-label={label}
      >
        <FileExclamationPoint className="h-4 w-4" aria-hidden />
      </span>
    </HoverPopover>
  );
}
